import React, { useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Button, Form } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router-dom';
import validator from 'validator';
import { Config } from '../config';

const CheckoutForm = (props: { email: string, isDeposit?: boolean, tipAmount?: number, totalAmount?: number }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [processing, setProcessing] = useState<boolean>(false);
  const returnUrl = Config.Env.PaymentSuccessfulReturnUrl;
  const { invoiceId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const handleSubmit = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    // Check to make sure that the user has entered a valid email address
    if (validator.isEmail(props.email) === false) {
      setErrorMessage("Please enter a valid email address to send the receipt to.");
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.      
      return;
    }

    setProcessing(true);
    setErrorMessage(undefined);

    // Build return URL back to the invoice page with email param
    // Preserve existing query params (isDeposit, connectedAccountId, etc.)
    const returnParams = new URLSearchParams(searchParams);
    returnParams.set('email', props.email);
    const invoiceReturnUrl = `${returnUrl}/invoice/${invoiceId}?${returnParams.toString()}`;

    const confirmParams: any = {
      return_url: invoiceReturnUrl,
      payment_method_data: {
        billing_details: {
          email: props.email,
        },
      }
    };
    
    // Add metadata for deposit payments and tips
    const metadata: any = {
      invoice_id: invoiceId,
    };
    
    if (props.isDeposit) {
      metadata.payment_type = 'solar_screen_deposit';
    }
    
    if (props.tipAmount && props.tipAmount > 0) {
      metadata.tip_amount = props.tipAmount.toString();
    }
    
    confirmParams.payment_method_data.metadata = metadata;
    
    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams
    });

    setProcessing(false);

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(error.message);
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="payment-form">
      {props.tipAmount && props.tipAmount > 0 && props.totalAmount && (
        <div className="alert alert-info mb-3">
          <div className="d-flex justify-content-between">
            <span>Service Amount:</span>
            <strong>${(props.totalAmount - props.tipAmount).toFixed(2)}</strong>
          </div>
          <div className="d-flex justify-content-between">
            <span>Tip:</span>
            <strong>${props.tipAmount.toFixed(2)}</strong>
          </div>
          <hr className="my-2" />
          <div className="d-flex justify-content-between">
            <span className="fw-bold">Total to Pay:</span>
            <strong className="text-primary">${props.totalAmount.toFixed(2)}</strong>
          </div>
        </div>
      )}
      
      <PaymentElement className="mb-4" />
      
      {errorMessage && (
        <div className="alert alert-warning mb-4">
          <div>{errorMessage}</div>
        </div>
      )}
      
      <div className="d-grid gap-2 mt-4">
        <Button 
          className={`modern-cta-button py-3 ${processing ? 'opacity-75' : ''} ${props.isDeposit ? 'bg-success border-success' : ''}`} 
          type="submit" 
          disabled={processing}
        >
          {processing 
            ? 'Processing...' 
            : props.isDeposit 
              ? `Pay Solar Screen Deposit - $${props.totalAmount?.toFixed(2) || ''}`
              : props.totalAmount
                ? `Pay $${props.totalAmount.toFixed(2)}`
                : 'Pay Now'
          }
        </Button>
      </div>
      
      <div className="text-center mt-3 text-muted">
        <small>Secure payment processing by Stripe</small>
      </div>
    </Form>
  );
};

export default CheckoutForm;